import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SortableList } from "@/components/admin/SortableList";

const uid = () => Math.random().toString(36).slice(2, 9);
const inp = "border border-stone-300 rounded-md px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-crimson/30 min-w-0";

const ChildRow = ({ c, update, remove, i, pid }) => (
  <div className="flex flex-wrap items-center gap-2">
    <input value={c.label} onChange={(e) => update({ label: e.target.value })} className={`${inp} flex-1`} placeholder="Label" data-testid={`menu-${pid}-child-label-${i}`} />
    <input value={c.url} onChange={(e) => update({ url: e.target.value })} className={`${inp} flex-1`} placeholder="/path" data-testid={`menu-${pid}-child-url-${i}`} />
    <Switch checked={c.visible !== false} onCheckedChange={(v) => update({ visible: v })} data-testid={`menu-${pid}-child-visible-${i}`} />
    <button onClick={remove} className="p-1 text-red-500" data-testid={`menu-${pid}-child-remove-${i}`} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
  </div>
);

const ItemRow = ({ item, update, remove, i }) => {
  const setChildren = (children) => update({ children });
  return (
    <div className="space-y-2 py-1">
      <div className="flex flex-wrap items-center gap-2">
        <input value={item.label} onChange={(e) => update({ label: e.target.value })} className={`${inp} flex-1`} placeholder="Label" data-testid={`menu-item-label-${i}`} />
        <select value={item.type} onChange={(e) => update({ type: e.target.value })} className={inp} data-testid={`menu-item-type-${i}`}>
          <option value="link">Link</option>
          <option value="dropdown">Dropdown</option>
          <option value="mega">Hotels mega menu</option>
        </select>
        {item.type === "link" && <input value={item.url} onChange={(e) => update({ url: e.target.value })} className={`${inp} flex-1`} placeholder="/path or https://" data-testid={`menu-item-url-${i}`} />}
        <label className="flex items-center gap-1.5 text-xs text-stone-500"><Switch checked={item.visible !== false} onCheckedChange={(v) => update({ visible: v })} data-testid={`menu-item-visible-${i}`} />Visible</label>
        <button onClick={remove} className="p-1 text-red-500" data-testid={`menu-item-remove-${i}`} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
      </div>
      {item.type === "dropdown" && (
        <div className="pl-4 border-l-2 border-stone-200 space-y-2">
          <SortableList
            items={item.children || []}
            onChange={setChildren}
            testid={`menu-${item.id}-children`}
            render={(c, j) => (
              <ChildRow c={c} i={j} pid={item.id}
                update={(p) => setChildren(item.children.map((x, k) => (k === j ? { ...x, ...p } : x)))}
                remove={() => setChildren(item.children.filter((_, k) => k !== j))} />
            )}
          />
          <button onClick={() => setChildren([...(item.children || []), { id: uid(), label: "New link", url: "/", visible: true }])} className="text-xs text-crimson font-bold inline-flex items-center gap-1" data-testid={`menu-${item.id}-add-child`}><Plus className="w-3.5 h-3.5" />Add sub-link</button>
        </div>
      )}
    </div>
  );
};

export default function MenuManager() {
  const { menu, reload } = useSite();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setItems(menu.map((m) => ({ children: [], ...m })));
  }, [menu]);

  const add = (type) => setItems([...items, { id: uid(), label: type === "link" ? "New link" : "New dropdown", type, url: type === "link" ? "/" : "", visible: true, children: [] }]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/menu", { items });
      await reload();
      toast.success("Menu saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-menu">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Menu Manager</h1>
          <p className="text-sm text-stone-500 mt-1">Rename, hide/show, reorder (drag or arrows) and add links or dropdowns.</p>
        </div>
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white rounded-md px-5 py-2.5 text-sm font-bold disabled:opacity-60" data-testid="menu-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save Menu
        </button>
      </div>
      <SortableList
        items={items}
        onChange={setItems}
        testid="menu-items"
        render={(it, i) => (
          <ItemRow item={it} i={i}
            update={(p) => setItems(items.map((x, k) => (k === i ? { ...x, ...p } : x)))}
            remove={() => setItems(items.filter((_, k) => k !== i))} />
        )}
      />
      <div className="flex gap-2 mt-4">
        <button onClick={() => add("link")} className="inline-flex items-center gap-1.5 text-sm border border-stone-300 bg-white rounded-md px-3 py-2" data-testid="menu-add-link"><Plus className="w-4 h-4" />Add link</button>
        <button onClick={() => add("dropdown")} className="inline-flex items-center gap-1.5 text-sm border border-stone-300 bg-white rounded-md px-3 py-2" data-testid="menu-add-dropdown"><Plus className="w-4 h-4" />Add dropdown</button>
      </div>
    </div>
  );
}
